<div
  class="doc-list-root"
  [ngClass]="{ 'document-list--embedded': embeddedInProject }"
>
  <!-- Header -->
  <div class="doc-header" *ngIf="!embeddedInProject">
    <div>
      <h1 class="doc-header__title">Document Management</h1>
      <p class="doc-header__subtitle">Manage and organize your construction documents</p>
    </div>
    <div class="doc-header__stat">
      <mat-icon class="doc-header__stat-icon">folder</mat-icon>
      <div>
        <p class="doc-header__stat-label">{{ isDeletedView ? 'Deleted' : 'Total' }}</p>
        <p class="doc-header__stat-value">{{ totalItems }}</p>
      </div>
    </div>
  </div>

  <div class="doc-header doc-header--compact" *ngIf="embeddedInProject">
    <div>
      <h2 class="doc-header__title doc-header__title--sm">Project Documents</h2>
      <p class="doc-header__subtitle">{{ getProjectDisplayName() }}</p>
    </div>
    <span class="doc-header__badge">{{ totalItems }} {{ isDeletedView ? 'deleted' : 'files' }}</span>
  </div>

  <!-- Filters -->
  <div class="doc-filters">
    <div class="doc-filters__search">
      <mat-icon class="doc-filters__search-icon">search</mat-icon>
      <input
        type="text"
        placeholder="Search by name, description, or tags..."
        [value]="searchKeyword"
        (input)="applyFilterFromSearch($event)"
      />
    </div>

    <select
      class="doc-filters__select"
      [(ngModel)]="selectedCategory"
      (change)="onCategoryFilterChange()"
    >
      <option value="">All Categories</option>
      <option *ngFor="let category of categories" [value]="category.id + ''">
        {{ category.category_name }}
      </option>
    </select>

    <select
      class="doc-filters__select"
      [(ngModel)]="selectedProject"
      (ngModelChange)="onProjectChange($event)"
      [disabled]="!!routeProjectId || embeddedInProject"
      *ngIf="!embeddedInProject"
    >
      <option value="">All Projects</option>
      <option *ngFor="let project of projects" [value]="project.id + ''">
        {{ project.project_name }}
      </option>
    </select>

    <div class="doc-filters__actions">
      <button type="button" class="doc-btn doc-btn--ghost" (click)="clearFilters()">Clear</button>
      <button type="button" class="doc-btn doc-btn--ghost" (click)="loadDocuments()">
        <mat-icon>refresh</mat-icon>
      </button>
      <button
        type="button"
        class="doc-btn doc-btn--primary"
        (click)="openUploadDialog()"
        [disabled]="isDeletedView"
      >
        <mat-icon>upload</mat-icon>
        Upload
      </button>
    </div>
  </div>

  <!-- Status chips + sort -->
  <div class="doc-toolbar">
    <div class="doc-toolbar__chips">
      <button
        type="button"
        class="doc-chip"
        [class.doc-chip--active]="statusFilter === 'active'"
        (click)="setStatusFilter('active')"
      >
        Active
      </button>
      <button
        type="button"
        class="doc-chip"
        [class.doc-chip--active]="statusFilter === 'deleted'"
        (click)="setStatusFilter('deleted')"
      >
        <mat-icon class="doc-chip__icon">restore</mat-icon>
        Deleted / Restore
      </button>
      <span class="doc-toolbar__divider" *ngIf="!isDeletedView"></span>
      <button
        type="button"
        class="doc-chip doc-chip--tag"
        *ngIf="!isDeletedView"
        [class.doc-chip--active]="filterFavoritesOnly"
        (click)="toggleFavoritesFilter()"
      >
        <mat-icon class="doc-chip__icon">star</mat-icon>
        Favorites
      </button>
      <button
        type="button"
        class="doc-chip doc-chip--tag"
        *ngIf="!isDeletedView"
        [class.doc-chip--active]="filterSharedOnly"
        (click)="toggleSharedFilter()"
      >
        <mat-icon class="doc-chip__icon">group</mat-icon>
        Shared
      </button>
      <button
        type="button"
        class="doc-chip doc-chip--tag"
        *ngFor="let tag of tagFilterChips"
        [class.doc-chip--active]="activeTagFilter === tag"
        [class.doc-chip--confidential]="tag === 'Confidential' && activeTagFilter !== tag"
        (click)="toggleTagFilter(tag)"
      >
        {{ tag }}
      </button>
    </div>
    <div class="doc-toolbar__sort">
      <button
        type="button"
        class="doc-btn doc-btn--ghost doc-btn--sm"
        [class.doc-btn--active]="sortBy === 'document_name'"
        (click)="toggleNameSort()"
      >
        <mat-icon>sort_by_alpha</mat-icon>
        {{ sortBy === 'document_name' ? sortLabel : 'Sort A–Z' }}
      </button>
      <button
        type="button"
        class="doc-btn doc-btn--ghost doc-btn--sm"
        [class.doc-btn--active]="sortBy === 'created_at'"
        (click)="setNewestSort()"
      >
        <mat-icon>schedule</mat-icon>
        Newest
      </button>
    </div>
  </div>

  <!-- Bulk actions -->
  <div class="doc-bulk" *ngIf="selectedCount > 0">
    <span class="doc-bulk__count">{{ selectedCount }} selected</span>
    <div class="doc-bulk__actions">
      <button
        type="button"
        class="doc-btn doc-btn--ghost doc-btn--sm"
        (click)="bulkDownload()"
        [disabled]="bulkActionInProgress"
        *ngIf="!isDeletedView"
      >
        <mat-icon>download</mat-icon>
        Download
      </button>
      <button
        type="button"
        class="doc-btn doc-btn--ghost doc-btn--sm"
        (click)="bulkDelete()"
        [disabled]="bulkActionInProgress"
        *ngIf="!isDeletedView"
      >
        <mat-icon>delete</mat-icon>
        Delete
      </button>
      <button
        type="button"
        class="doc-btn doc-btn--ghost doc-btn--sm"
        (click)="bulkRestore()"
        [disabled]="bulkActionInProgress"
        *ngIf="isDeletedView"
      >
        <mat-icon>restore</mat-icon>
        Restore
      </button>
      <button type="button" class="doc-btn doc-btn--ghost doc-btn--sm" (click)="selection.clear()">
        Clear selection
      </button>
    </div>
  </div>

  <!-- Main workspace: table + sidebar -->
  <div class="doc-workspace">
    <div class="doc-main">
      <div class="doc-table-card">
        <div *ngIf="loading" class="doc-loading">
          <div class="doc-loading__spinner"></div>
          <p>Loading documents...</p>
        </div>

        <div *ngIf="!loading && dataSource.data.length > 0" class="doc-table-wrap">
          <table mat-table [dataSource]="dataSource" class="doc-table w-full">
            <ng-container matColumnDef="select">
              <th mat-header-cell *matHeaderCellDef class="doc-th doc-th--check">
                <mat-checkbox
                  (change)="$event ? masterToggle() : null"
                  [checked]="isAllSelected()"
                  [indeterminate]="selectedCount > 0 && !isAllSelected()"
                ></mat-checkbox>
              </th>
              <td mat-cell *matCellDef="let document" class="doc-td doc-td--check" (click)="$event.stopPropagation()">
                <mat-checkbox
                  (click)="$event.stopPropagation()"
                  (change)="$event ? selection.toggle(document) : null"
                  [checked]="selection.isSelected(document)"
                ></mat-checkbox>
              </td>
            </ng-container>

            <ng-container matColumnDef="id">
              <th mat-header-cell *matHeaderCellDef class="doc-th">ID</th>
              <td mat-cell *matCellDef="let document" class="doc-td doc-td--id">
                #{{ document.id }}
              </td>
            </ng-container>

            <ng-container matColumnDef="document_name">
              <th mat-header-cell *matHeaderCellDef class="doc-th">Name</th>
              <td mat-cell *matCellDef="let document" class="doc-td doc-cell-name">
                <div class="doc-name-cell">
                  <div class="doc-name-cell__icon">
                    <mat-icon>{{ getFileIcon(document.file_type) }}</mat-icon>
                  </div>
                  <div class="doc-name-cell__text">
                    <span class="doc-name-cell__title" [matTooltip]="document.document_name">
                      {{ document.document_name }}
                    </span>
                    <span class="doc-name-cell__badges">
                      <span class="doc-badge doc-badge--shared" *ngIf="isDocumentShared(document)">Shared</span>
                      <span
                        class="doc-badge doc-badge--tag"
                        *ngFor="let tag of getDocumentTags(document).slice(0, 2)"
                      >{{ tag }}</span>
                    </span>
                    <span class="doc-name-cell__file" *ngIf="document.file_name">{{ document.file_name }}</span>
                  </div>
                </div>
              </td>
            </ng-container>

            <ng-container matColumnDef="lrs_corridor">
              <th mat-header-cell *matHeaderCellDef class="doc-th">LRS corridor</th>
              <td mat-cell *matCellDef="let document" class="doc-td doc-td--mono">
                <span class="doc-lrs-pill" *ngIf="getDocumentLrs(document)['route_id']; else noLrs">
                  {{ formatLrsCorridorLabel(document) }}
                </span>
                <ng-template #noLrs>—</ng-template>
              </td>
            </ng-container>

            <ng-container matColumnDef="category">
              <th mat-header-cell *matHeaderCellDef class="doc-th">Category</th>
              <td mat-cell *matCellDef="let document" class="doc-td">
                <span class="doc-pill">{{ document.category_name || 'Uncategorized' }}</span>
              </td>
            </ng-container>

            <ng-container matColumnDef="file_type">
              <th mat-header-cell *matHeaderCellDef class="doc-th">Type</th>
              <td mat-cell *matCellDef="let document" class="doc-td doc-td--mono">
                {{ document.file_extension?.toUpperCase() || '—' }}
              </td>
            </ng-container>

            <ng-container matColumnDef="file_size">
              <th mat-header-cell *matHeaderCellDef class="doc-th">Size</th>
              <td mat-cell *matCellDef="let document" class="doc-td doc-td--mono">
                {{ formatFileSize(document.file_size) }}
              </td>
            </ng-container>

            <ng-container matColumnDef="updated_at">
              <th mat-header-cell *matHeaderCellDef class="doc-th">Modified</th>
              <td mat-cell *matCellDef="let document" class="doc-td doc-td--date">
                <time [attr.datetime]="document.updated_at">
                  {{ document.updated_at | date:'MMM d, y' }}
                </time>
                <span class="doc-td__sub">{{ document.updated_at | date:'h:mm a' }}</span>
              </td>
            </ng-container>

            <ng-container matColumnDef="uploaded_by">
              <th mat-header-cell *matHeaderCellDef class="doc-th">Uploaded by</th>
              <td mat-cell *matCellDef="let document" class="doc-td">
                <div class="doc-uploader-stack">
                  <span class="doc-uploader-name">{{ document.uploaded_by_name || 'Unknown' }}</span>
                  <time class="doc-uploader-time" [attr.datetime]="document.created_at">
                    {{ document.created_at | date:'MMM d, y' }}
                  </time>
                </div>
              </td>
            </ng-container>

            <ng-container matColumnDef="actions">
              <th mat-header-cell *matHeaderCellDef class="doc-th doc-th--actions">Actions</th>
              <td mat-cell *matCellDef="let document" class="doc-td doc-td--actions" (click)="$event.stopPropagation()">
                <div class="doc-row-actions">
                  <button
                    type="button"
                    *ngIf="!isDeletedView"
                    (click)="toggleFavorite(document)"
                    class="doc-icon-btn"
                    [class.doc-icon-btn--fav]="document.is_favorite"
                    [matTooltip]="document.is_favorite ? 'Remove favorite' : 'Favorite'"
                  >
                    <mat-icon>{{ document.is_favorite ? 'star' : 'star_border' }}</mat-icon>
                  </button>
                  <button
                    type="button"
                    (click)="viewDocument(document)"
                    class="doc-icon-btn"
                    [matTooltip]="isCadDocument(document) ? 'Open in CAD Viewer' : 'Preview'"
                  >
                    <mat-icon>{{ isCadDocument(document) ? 'architecture' : 'visibility' }}</mat-icon>
                  </button>
                  <button
                    type="button"
                    (click)="downloadDocument(document)"
                    class="doc-icon-btn"
                    matTooltip="Download"
                    *ngIf="!isDeletedView"
                  >
                    <mat-icon>download</mat-icon>
                  </button>
                  <button
                    type="button"
                    (click)="restoreDocument(document)"
                    class="doc-icon-btn doc-icon-btn--restore"
                    matTooltip="Restore"
                    *ngIf="isDeletedView"
                  >
                    <mat-icon>restore</mat-icon>
                  </button>
                  <button
                    type="button"
                    (click)="deleteDocument(document)"
                    class="doc-icon-btn doc-icon-btn--danger"
                    matTooltip="Delete"
                    *ngIf="!isDeletedView"
                  >
                    <mat-icon>delete</mat-icon>
                  </button>
                </div>
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
            <tr
              mat-row
              *matRowDef="let row; columns: displayedColumns;"
              class="doc-row"
              [class.doc-row--selected]="isRowActive(row)"
              (click)="onRowClick(row, $event)"
            ></tr>
          </table>
        </div>

        <div *ngIf="!loading && dataSource.data.length === 0" class="doc-empty">
          <mat-icon class="doc-empty__icon">folder_open</mat-icon>
          <h3>{{ isDeletedView ? 'No deleted documents' : 'No documents found' }}</h3>
          <p>{{ isDeletedView ? 'Deleted files will appear here for restore.' : 'Upload your first document to get started.' }}</p>
          <button
            type="button"
            class="doc-btn doc-btn--primary"
            (click)="openUploadDialog()"
            *ngIf="!isDeletedView"
          >
            Upload Document
          </button>
        </div>

        <div *ngIf="!loading && totalItems > 0" class="doc-paginator-bar">
          <span class="doc-paginator-bar__text">
            {{ pageIndex * pageSize + 1 }}–{{ Math.min((pageIndex + 1) * pageSize, totalItems) }} of {{ totalItems }}
          </span>
          <mat-paginator
            [length]="totalItems"
            [pageSize]="pageSize"
            [pageIndex]="pageIndex"
            [pageSizeOptions]="pageSizeOptions"
            [showFirstLastButtons]="true"
            (page)="onPageChange($event)"
            class="doc-paginator"
          ></mat-paginator>
        </div>
      </div>
    </div>

    <!-- Detail sidebar -->
    <aside class="doc-sidebar" [class.doc-sidebar--visible]="!!selectedDocument">
      <div class="doc-sidebar__inner" *ngIf="selectedDocument; else sidebarEmpty">
        <div class="doc-sidebar__header">
          <div class="doc-sidebar__icon-wrap">
            <mat-icon>{{ getFileIcon(selectedDocument.file_type) }}</mat-icon>
          </div>
          <div class="doc-sidebar__titles">
            <h3 class="doc-sidebar__name">{{ selectedDocument.document_name }}</h3>
            <p class="doc-sidebar__id">#{{ selectedDocument.id }} · {{ selectedDocument.file_name }}</p>
          </div>
          <button type="button" class="doc-sidebar__close" (click)="clearDetailSelection()" matTooltip="Close">
            <mat-icon>close</mat-icon>
          </button>
        </div>

        <div *ngIf="detailLoading" class="doc-sidebar__loading">
          <div class="doc-loading__spinner doc-loading__spinner--sm"></div>
        </div>

        <div class="doc-sidebar__body" *ngIf="!detailLoading">
          <dl class="doc-meta-list">
            <div class="doc-meta-list__item">
              <dt>Category</dt>
              <dd>{{ documentDetail?.category_name || selectedDocument.category_name || 'Uncategorized' }}</dd>
            </div>
            <div class="doc-meta-list__item">
              <dt>Type / Size</dt>
              <dd>
                {{ selectedDocument.file_extension?.toUpperCase() || '—' }}
                · {{ formatFileSize(selectedDocument.file_size) }}
              </dd>
            </div>
            <div class="doc-meta-list__item">
              <dt>Uploaded</dt>
              <dd>
                {{ selectedDocument.created_at | date:'medium' }}
                <span class="doc-meta-list__sub">by {{ selectedDocument.uploaded_by_name || 'Unknown' }}</span>
              </dd>
            </div>
            <div class="doc-meta-list__item">
              <dt>Modified</dt>
              <dd>{{ selectedDocument.updated_at | date:'medium' }}</dd>
            </div>
            <div class="doc-meta-list__item" *ngIf="selectedProject">
              <dt>Project</dt>
              <dd>{{ getProjectDisplayName() }}</dd>
            </div>
            <div class="doc-meta-list__item" *ngIf="selectedDocument.description">
              <dt>Description</dt>
              <dd class="doc-meta-list__desc">{{ selectedDocument.description }}</dd>
            </div>
          </dl>

          <div class="doc-sidebar__section" *ngIf="!isDeletedView">
            <div class="doc-sidebar__section-head">
              <h4 class="doc-sidebar__section-title">Tags</h4>
              <button
                type="button"
                class="doc-btn doc-btn--ghost doc-btn--sm"
                (click)="saveTags()"
                [disabled]="tagsSaving"
              >
                {{ tagsSaving ? 'Saving…' : 'Save tags' }}
              </button>
            </div>
            <div class="doc-tag-list doc-tag-list--editable">
              <span class="doc-tag doc-tag--removable" *ngFor="let tag of editableTags">
                {{ tag }}
                <button type="button" class="doc-tag__remove" (click)="removeEditableTag(tag)" aria-label="Remove tag">
                  <mat-icon>close</mat-icon>
                </button>
              </span>
              <span class="doc-tag doc-tag--muted" *ngIf="!editableTags.length">No tags</span>
            </div>
            <div class="doc-tag-quick">
              <button
                type="button"
                class="doc-chip doc-chip--xs"
                *ngFor="let tag of quickTagFilters"
                (click)="quickAddSidebarTag(tag)"
              >+ {{ tag }}</button>
            </div>
            <div class="doc-tag-input-row">
              <input
                type="text"
                class="doc-tag-input"
                placeholder="Add tag…"
                [(ngModel)]="sidebarTagInput"
                (keydown.enter)="addSidebarTag(); $event.preventDefault()"
              />
              <button type="button" class="doc-btn doc-btn--ghost doc-btn--sm" (click)="addSidebarTag()">Add</button>
            </div>
          </div>

          <div class="doc-sidebar__section" *ngIf="!isDeletedView">
            <div class="doc-sidebar__section-head">
              <h4 class="doc-sidebar__section-title">Access & permissions</h4>
              <button
                type="button"
                class="doc-btn doc-btn--ghost doc-btn--sm"
                (click)="togglePermissionsPanel()"
              >
                {{ permissionsPanelOpen ? 'Collapse' : 'Manage' }}
              </button>
            </div>
            <p class="doc-perm-summary">{{ getPermissionSummary(documentDetail || selectedDocument) }}</p>
            <ul class="doc-perm-list" *ngIf="isDocumentShared(documentDetail || selectedDocument) && !permissionsPanelOpen">
              <li *ngFor="let perm of documentDetail?.permissions || []">
                <span class="doc-perm-list__who">
                  {{ perm.role_name || perm.user_name || getRoleName(perm.role_id) }}
                </span>
                <span class="doc-perm-list__type">{{ perm.permission_type }}</span>
              </li>
            </ul>
            <div class="doc-perm-editor" *ngIf="permissionsPanelOpen">
              <label class="doc-perm-editor__org">
                <mat-checkbox
                  [checked]="orgWideAccess"
                  (change)="setOrgWideAccess($event.checked)"
                ></mat-checkbox>
                Organization-wide access (no role restrictions)
              </label>
              <p class="doc-perm-editor__hint" *ngIf="orgWideAccess">
                Org admins can access by default. Add rules below to restrict to specific roles.
              </p>
              <div class="doc-perm-rows" *ngIf="!orgWideAccess">
                <div class="doc-perm-row" *ngFor="let row of permissionRows; let i = index">
                  <label class="doc-perm-select-group">
                    <span class="doc-perm-select-label">Role</span>
                    <select
                      class="doc-perm-select"
                      [(ngModel)]="row.role_id"
                      [ngModelOptions]="{ standalone: true }"
                    >
                      <option [ngValue]="null" disabled>Select role</option>
                      <option *ngFor="let role of orgRoles" [ngValue]="role.role_id">
                        {{ role.role_name }}
                      </option>
                    </select>
                  </label>
                  <label class="doc-perm-select-group doc-perm-select-group--access">
                    <span class="doc-perm-select-label">Access</span>
                    <select
                      class="doc-perm-select"
                      [(ngModel)]="row.permission_type"
                      [ngModelOptions]="{ standalone: true }"
                    >
                      <option *ngFor="let pt of permissionTypes" [value]="pt">{{ pt }}</option>
                    </select>
                  </label>
                  <button
                    type="button"
                    class="doc-icon-btn doc-icon-btn--danger doc-perm-row__remove"
                    (click)="removePermissionRow(i)"
                    matTooltip="Remove rule"
                    aria-label="Remove permission rule"
                  >
                    <mat-icon>remove_circle_outline</mat-icon>
                  </button>
                </div>
                <button type="button" class="doc-btn doc-btn--ghost doc-btn--sm" (click)="addPermissionRow()">
                  <mat-icon>add</mat-icon>
                  Add role rule
                </button>
              </div>
              <button
                type="button"
                class="doc-btn doc-btn--primary doc-btn--block doc-perm-save"
                (click)="savePermissions()"
                [disabled]="permissionsSaving"
              >
                {{ permissionsSaving ? 'Saving…' : 'Save permissions' }}
              </button>
            </div>
          </div>

          <div class="doc-sidebar__section" *ngIf="!isDeletedView">
            <div class="doc-sidebar__section-head">
              <h4 class="doc-sidebar__section-title">LRS chainage</h4>
            </div>
            <p class="doc-lrs-hint">
              <ng-container *ngIf="lrsRouteId; else noLrsYet">
                Linked to route #{{ lrsRouteId }}
                <span *ngIf="lrsChainageLabel"> · {{ lrsChainageLabel }}</span>
              </ng-container>
              <ng-template #noLrsYet>Tag this document to a highway route and chainage.</ng-template>
            </p>
            <label class="doc-lrs-field">
              <span>Route</span>
              <select [(ngModel)]="lrsRouteId">
                <option [ngValue]="null">Select route…</option>
                <option *ngFor="let route of lrsRoutes" [ngValue]="route.id">
                  {{ route.route_code }} — {{ route.route_name }}
                </option>
              </select>
            </label>
            <div class="doc-lrs-grid">
              <label class="doc-lrs-field">
                <span>From km</span>
                <input type="number" step="0.001" [(ngModel)]="lrsFromKm" />
              </label>
              <label class="doc-lrs-field">
                <span>To km</span>
                <input type="number" step="0.001" [(ngModel)]="lrsToKm" />
              </label>
              <label class="doc-lrs-field">
                <span>Measure km</span>
                <input type="number" step="0.001" [(ngModel)]="lrsMeasureKm" />
              </label>
              <label class="doc-lrs-field doc-lrs-field--full">
                <span>Chainage label</span>
                <input type="text" [(ngModel)]="lrsChainageLabel" placeholder="e.g. NH-44 @ 120+750" />
              </label>
            </div>
            <div class="doc-lrs-actions">
              <button type="button" class="doc-btn doc-btn--ghost" (click)="openDocumentOnMap()">
                <mat-icon>map</mat-icon>
                Open on map
              </button>
              <button
                type="button"
                class="doc-btn doc-btn--primary"
                (click)="saveDocumentLrs()"
                [disabled]="lrsSaving || !lrsRouteId"
              >
                {{ lrsSaving ? 'Saving…' : 'Save LRS link' }}
              </button>
            </div>
          </div>

          <div
            class="doc-sidebar__section"
            *ngIf="(getDocumentMetadata(documentDetail || selectedDocument) | keyvalue).length"
          >
            <h4 class="doc-sidebar__section-title">Metadata</h4>
            <dl class="doc-meta-list doc-meta-list--compact">
              <div
                class="doc-meta-list__item"
                *ngFor="let entry of getDocumentMetadata(documentDetail || selectedDocument) | keyvalue"
              >
                <dt>{{ entry.key }}</dt>
                <dd>{{ entry.value }}</dd>
              </div>
            </dl>
          </div>

          <div class="doc-sidebar__footer">
            <button
              type="button"
              class="doc-btn doc-btn--primary doc-btn--block"
              (click)="viewDocument(selectedDocument)"
              *ngIf="!isDeletedView"
            >
              <mat-icon>{{ isCadDocument(selectedDocument) ? 'architecture' : 'visibility' }}</mat-icon>
              {{ isCadDocument(selectedDocument) ? 'Open in CAD Viewer' : 'Open preview' }}
            </button>
            <button
              type="button"
              class="doc-btn doc-btn--ghost doc-btn--block"
              (click)="downloadDocument(selectedDocument)"
              *ngIf="!isDeletedView"
            >
              <mat-icon>download</mat-icon>
              Download
            </button>
            <button
              type="button"
              class="doc-btn doc-btn--ghost doc-btn--block"
              (click)="restoreDocument(selectedDocument)"
              *ngIf="isDeletedView"
            >
              <mat-icon>restore</mat-icon>
              Restore document
            </button>
            <button
              type="button"
              class="doc-btn doc-btn--danger-ghost doc-btn--block"
              (click)="deleteDocument(selectedDocument)"
              *ngIf="!isDeletedView"
            >
              <mat-icon>delete</mat-icon>
              Delete
            </button>
          </div>
        </div>
      </div>

      <ng-template #sidebarEmpty>
        <div class="doc-sidebar__empty">
          <mat-icon>touch_app</mat-icon>
          <p>Select a document to view details</p>
        </div>
      </ng-template>
    </aside>
  </div>
</div>
